import React from "react";
import ReactHtmlParser from "react-html-parser";
import replaceImageCdn from "@herb/utils/replaceImageCdn";
import "./index.css";

/**
 * Serves the hero background through imgix at the width it is painted at.
 *
 * `fit=max` is deliberate: it caps at the asset's own dimensions and never
 * enlarges. The hero images currently in WordPress are 1x exports of the Figma
 * frames (1280x459 desktop, 480x338 mobile), so asking for anything larger
 * would only re-encode an upscale. Replacing them with 2x exports is what will
 * actually sharpen this on wide and high-DPI screens.
 */
const sizedImage = (url, width) => {
  const source = replaceImageCdn(url || "");

  if (!source) {
    return source;
  }

  return `${source}${
    source.includes("?") ? "&" : "?"
  }w=${width}&fit=max&auto=compress,format&q=82`;
};

/**
 * The heading is authored in a WYSIWYG field, so WordPress wraps it in
 * paragraph tags. An <h1> only accepts phrasing content, so the wrappers are
 * unpicked here — paragraph breaks become <br /> — while the inline formatting
 * the editor applied (<em>, <strong>) is kept and styled by index.css.
 */
const asHeadingMarkup = html =>
  (html || "")
    .trim()
    .replace(/<\/p>\s*<p[^>]*>/gi, "<br />")
    .replace(/<\/?p[^>]*>/gi, "")
    .trim();

/**
 * Hemp Ban hero. The heading is parsed as HTML so an editor can emphasise a
 * word in WordPress (the design italicises "Date") without the markup being
 * hardcoded here.
 */
const HempBanHero = ({
  heroBanner,
  ctaFallbackHref = "#hemp-ban-key-dates"
}) => {
  if (!heroBanner) {
    return null;
  }

  const {
    heading,
    subheading,
    heroFooterText,
    button,
    backgroundImage,
    backgroundImageMobile
  } = heroBanner;

  const desktopImage = backgroundImage?.sourceUrl;
  const mobileImage = backgroundImageMobile?.sourceUrl || desktopImage;
  const ctaHref = button?.buttonLink || ctaFallbackHref;
  const isExternal = /^https?:\/\//.test(ctaHref);

  return (
    <section className="hb-hero">
      <div className="hb-hero-media">
        {desktopImage ? (
          <picture>
            <source
              media="(max-width: 768px)"
              srcSet={sizedImage(mobileImage, 960)}
            />
            <img
              className="hb-hero-image"
              src={sizedImage(desktopImage, 1920)}
              alt={backgroundImage?.altText || ""}
              loading="eager"
            />
          </picture>
        ) : null}
        <span className="hb-hero-scrim" aria-hidden="true" />
      </div>

      <div className="hb-hero-body">
        {heading ? (
          <h1 className="hb-hero-title">
            {ReactHtmlParser(asHeadingMarkup(heading))}
          </h1>
        ) : null}
        {subheading ? <p className="hb-hero-sub">{subheading}</p> : null}
        {button?.buttonText ? (
          <a
            className="hb-hero-cta"
            href={ctaHref}
            {...(isExternal
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
          >
            {button.buttonText}
          </a>
        ) : null}
      </div>

      {heroFooterText ? (
        <p className="hb-hero-footnote">
          <span className="hb-hero-footnote-text">{heroFooterText}</span>
        </p>
      ) : null}
    </section>
  );
};

export default HempBanHero;
